import React from 'react';
import {View} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import Text from '/component/base/Text';
import {DashboardID} from '/constant/TestIDs';
import {REPORT_BUSINESS_BUILDER_COACHES} from '/enum/records.enum';
import {EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {InlineFormattedNumber} from '../../FormattedNumber';
import Icon from '../../Icon';
import ProgressBar from '../../ProgressBar';
import TouchableOpacity from '../../TouchableOpacity';
import {getDaysLeftForBonus} from '../BonusProgressBar.util';

import {useBonusProgressBarBBB} from './BBBBannerProgressBar.hook';
import {styles} from './BBBBannerProgressBar.style';
import {
  higherIntermediateBBB,
  higherIntermediateFQV,
  isTextBold,
  lowerIntermediateBBB,
  lowerIntermediateFQV,
  maximumBBB,
  maximumFQV,
  minimumBBB,
  minimumFQV,
} from './BBBBannerProgressBar.util';

interface BBBBannerProgressBarProps {
  businessBuliderCoachesCount: number;
  fqv: number;
  currentDate: string;
  isBanner: boolean;
  lastPaidRank: string;
}

export const BBBBannerProgressBar: React.FC<BBBBannerProgressBarProps> = ({
  businessBuliderCoachesCount = 0,
  fqv = 0,
  currentDate = '',
  isBanner = false,
  lastPaidRank = '',
}) => {
  const {progressArray} = useBonusProgressBarBBB({fqv});
  const {tt} = useTranslation('bonusProgressBarBB');
  const {navigate} = useNavigation();

  const dateUtil = getDateUtilDefaultInstance();
  const currentDateFormatted = dateUtil.formatDate(currentDate, 'MMMM yyyy');
  const daysLeftForBonus = getDaysLeftForBonus({currentDate});
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.onPageLoad,
  });

  const renderFQVValue = () => (
    <View style={styles.fqvContainer}>
      {progressArray?.map((item, index) => {
        if (index === 0) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMinThresholdLabel,
              )}>
              {minimumFQV} FQV
            </Text>
          );
        }
        if (index === 1) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={styles.fqvMargin}
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMidThresholdLabel,
              )}>
              {lowerIntermediateFQV} FQV
            </Text>
          );
        }
        if (index === 2) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={styles.fqvMargin}
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMidSecondThresholdLabel,
              )}>
              {higherIntermediateFQV} FQV
            </Text>
          );
        }
        if (index === 3) {
          return (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              variant="caption"
              style={styles.fqvMargin}
              key={index}
              {...testProperties(
                DashboardID.bonusProgressBarMaxThresholdLabel,
              )}>
              <InlineFormattedNumber
                textEmphasis="medium"
                value={Number(maximumFQV)}
                style={styles.maximumFQV}
              />{' '}
              FQV
            </Text>
          );
        }
        return <View key={index} />;
      })}
    </View>
  );

  const renderBonusPercent = () => {
    return (
      <View style={styles.bonusContainer}>
        {progressArray.map((item, index) => {
          if (index === 0) {
            return (
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                emphasis="medium"
                key={index}
                style={[isTextBold(minimumBBB, fqv) && styles.boldText]}
                {...testProperties(
                  DashboardID.bonusProgressBarMinPercentLabel,
                )}>
                {minimumBBB}
              </Text>
            );
          }
          if (index === 1) {
            return (
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                emphasis="medium"
                variant="caption"
                style={[
                  styles.lowerIntermediateBBB,
                  isTextBold(lowerIntermediateBBB, fqv) && styles.boldText,
                ]}
                key={index}
                {...testProperties(
                  DashboardID.bonusProgressBarMidPercentLabel,
                )}>
                {lowerIntermediateBBB}
              </Text>
            );
          }
          if (index === 2) {
            return (
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                emphasis="medium"
                variant="caption"
                style={[
                  styles.higherIntermediateBBB,
                  isTextBold(higherIntermediateBBB, fqv) && styles.boldText,
                ]}
                key={index}
                {...testProperties(
                  DashboardID.bonusProgressBarMidSecondPercentLabel,
                )}>
                {higherIntermediateBBB}
              </Text>
            );
          }
          if (index === 3) {
            return (
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                emphasis="medium"
                variant="caption"
                style={[
                  styles.higherIntermediateBBB,
                  isTextBold(maximumBBB, fqv) && styles.boldText,
                ]}
                key={index}
                {...testProperties(
                  DashboardID.bonusProgressBarMaxPercentLabel,
                )}>
                {maximumBBB}
              </Text>
            );
          }
          return <View key={index} />;
        })}
      </View>
    );
  };

  const renderDaysLeft = () => {
    if (daysLeftForBonus > 1) {
      return tt('daysLeft', () => (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.daysLeft}
          emphasis="medium"
          variant="caption"
          {...testProperties(DashboardID.dashboardProgressBarDaysLeft)}>
          {daysLeftForBonus} days left
        </Text>
      ));
    } else if (daysLeftForBonus === 1) {
      return tt('dayLeft', () => (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.daysLeft}
          emphasis="medium"
          variant="caption"
          {...testProperties(DashboardID.dashboardProgressBarDaysLeft)}>
          {daysLeftForBonus} day left
        </Text>
      ));
    }
    return tt('lastDay', () => (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        style={styles.daysLeft}
        emphasis="medium"
        variant="caption"
        {...testProperties(DashboardID.dashboardProgressBarDaysLeft)}>
        Last day
      </Text>
    ));
  };

  const renderBBCoachesSection = () => {
    return (
      <TouchableOpacity
        onPress={() => {
          sendEvent({
            currentScreenName: SCREEN_NAMES.contributingCoaches,
            previousScreenName: SCREEN_NAMES.dashboard,
            lastPaidRank: lastPaidRank,
          });
          navigate('card', {
            modalName: REPORT_BUSINESS_BUILDER_COACHES,
            modalProps: {},
          });
        }}
        {...testProperties(null, true)}>
        <View
          style={styles.coachesContainer}
          {...testProperties(DashboardID.bbBannerContributingCoaches)}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            variant="button"
            style={styles.coachesText}
            {...testProperties(DashboardID.bbBannerContributingCoachesBtn)}>
            {tt('contributingCoaches', 'Contributing Coaches')}{' '}
          </Text>
          <View style={styles.coachesCountContainer}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="button"
              style={styles.coachesCount}
              {...testProperties(DashboardID.bbBannerCoachesCount)}>
              {businessBuliderCoachesCount}
            </Text>
            <Icon name="chevron-right" style={styles.arrowIcon} />
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.rootContainer}>
      <View style={styles.heading}>
        <View style={styles.subHeading}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={styles.dateText}
            variant={isBanner ? 'heading' : 'small'}
            {...testProperties(DashboardID.dashboardProgressBarCurrentDate)}>
            {currentDateFormatted}
          </Text>
          {renderDaysLeft()}
        </View>
        {tt('fqvEarned', () => (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={styles.fqvText}
            {...testProperties(DashboardID.dashboardProgressBarEarned)}>
            <InlineFormattedNumber
              textEmphasis="high"
              value={!isNaN(fqv) ? fqv : 0}
              truncate
            />{' '}
            FQV
          </Text>
        ))}
      </View>
      <View style={styles.progressBarContainer}>
        {progressArray?.length > 0 &&
          progressArray?.map(({num, progress}) => (
            <ProgressBar
              key={num}
              progress={progress}
              style={{
                base: {
                  ...styles.progressBar,
                  flex: num === 0 || num === 4 ? 0.4 : 1,
                },
              }}
            />
          ))}
      </View>
      <View style={styles.bonusProgressContainer}>
        {renderBonusPercent()}
        {renderFQVValue()}
      </View>
      {renderBBCoachesSection()}
    </View>
  );
};
